/*
 * The retired hard-coded client forms. Their responses are kept as a read-only archive on the
 * backend (FM-BackEnd-v2 modules/archive): lists, single records and uploaded files, readable by
 * staff who can manage client forms. Nothing here can change or delete a response.
 *
 * Each field is [column, label, kind?]. kind: 'flag' (0/1 → Yes/No), 'date', 'long' (keeps line
 * breaks), 'money'. Files are listed separately with how to download them.
 */

const fullName = (...parts) => parts.filter(Boolean).join(' ').trim();

export const ARCHIVED_FORMS = [
  {
    key: 'rocknmalta',
    name: 'RockNMalta',
    what: 'Rock music fund applications',
    list: { url: '/api/rock-malta-fund/applications', key: 'applications' },
    when: 'created_at',
    exportUrl: '/api/excel-rnm/export',
    title: (r) => r.organisation_name || r.applicant_name || `Application #${r.id}`,
    subtitle: (r) => [r.organisation_name ? r.applicant_name : null, r.email_address].filter(Boolean).join(' · '),
    columns: [{ label: 'Category', value: (r) => r.category_of_applicant }],
    sections: [
      {
        title: 'Applicant',
        fields: [
          ['organisation_name', 'Organisation name'],
          ['representee', 'Representative'],
          ['applicant_name', 'Applicant name'],
          ['email_address', 'Email address'],
          ['contact_number', 'Contact number'],
          ['category_of_applicant', 'Category'],
          ['category_applicant_name', 'Category applicant name'],
        ],
      },
      {
        title: 'Project',
        fields: [
          ['event_description', 'Event description', 'long'],
          ['event_date', 'Event date'],
          ['artists_proposed', 'Artists proposed', 'long'],
          ['venue_proposed', 'Venue proposed'],
          ['business_plan', 'Business plan', 'long'],
          ['estimated_cost', 'Estimated cost', 'money'],
        ],
      },
      {
        title: 'Declarations',
        fields: [
          ['health_safety_declaration', 'Health and safety declaration'],
          ['declaration_gber', 'GBER declaration'],
          ['declaration_outstanding_orders', 'Outstanding recovery orders declaration'],
          ['declaration_operate', 'Legally entitled to operate in Malta', 'flag'],
          ['hard_copy_submitted', 'Hard copy submitted', 'flag'],
          ['soft_copy_submitted', 'Soft copy submitted', 'flag'],
        ],
      },
    ],
    files: [
      ['applicant_id_card', 'ID card (front)'],
      ['applicant_id_card_2', 'ID card (back)'],
      ['tax_compliance_doc', 'Tax compliance document'],
      ['de_minimis_declaration_form', 'State aid declaration'],
      ['organisation_structure_history', 'Organisation role chart'],
      ['organisational_role_chart', 'Organisational role chart'],
      ['project_management_plan', 'Project management plan'],
      ['marketing_plan', 'Marketing plan'],
      ['upload_financial_model', 'Business financial model'],
      ['upload_ticket_prices', 'Ticket prices'],
      ['upload_comprehensive_list', 'Revenue streams'],
      ['upload_itemised_list', 'Expenses and costs'],
      ['upload_projected_pl', 'Profit and loss report'],
      ['upload_anticipated_audience', 'Anticipated audience'],
      ['upload_declaration_intent', 'Insurance declaration'],
    ].map(([column, label]) => ({
      column,
      label,
      url: (r) => (r[column] ? `/api/rock-malta-fund/uploads/${encodeURIComponent(r[column])}` : null),
      name: (r) => r[column],
    })),
  },
  {
    key: 'danceon',
    name: 'Dance On',
    what: 'Dance On sign-ups',
    list: { url: '/api/danceon/submissions', key: 'submissions' },
    when: 'submittedAt',
    title: (r) => fullName(r.name, r.surname) || `Submission #${r.id}`,
    subtitle: (r) => r.email,
    columns: [{ label: 'Interest', value: (r) => r.interest }],
    sections: [
      {
        title: 'Applicant',
        fields: [
          ['name', 'Name'],
          ['surname', 'Surname'],
          ['age', 'Age'],
          ['email', 'Email'],
          ['contact', 'Contact'],
        ],
      },
      {
        title: 'About them',
        fields: [
          ['interest', 'Interest'],
          ['background', 'Background', 'long'],
        ],
      },
    ],
    files: [],
  },
  {
    key: 'ghannejja',
    name: 'Għannejja Ġodda',
    what: 'Għannejja Ġodda applications',
    list: { url: '/api/ghannejja/applications', key: 'applications' },
    when: 'submittedAt',
    title: (r) => fullName(r.fName, r.lName) || `Application #${r.id}`,
    subtitle: (r) => r.email,
    columns: [
      { label: 'Locality', value: (r) => r.localityName || r.locality },
      { label: 'Payment', value: (r) => (Number(r.hasPaid) === 1 ? 'Paid' : 'Not paid') },
    ],
    sections: [
      {
        title: 'Applicant',
        fields: [
          ['fName', 'Name'],
          ['lName', 'Surname'],
          ['age', 'Age'],
          ['email', 'Email'],
          ['telNo', 'Telephone'],
          ['idCard', 'ID card'],
          ['address', 'Address'],
          ['localityName', 'Locality'],
        ],
      },
      {
        title: 'Application',
        fields: [
          ['interest', 'Interest', 'long'],
          ['option1', 'Familjari mad-dinja tal-għana', 'flag'],
          ['option2', 'Nkanta ġeneri oħrajn', 'flag'],
          ['option3', 'Ma nkantax', 'flag'],
          ['hasPaid', 'Paid the €5 fee', 'flag'],
        ],
      },
      {
        title: 'Guardian (under 16s)',
        when: (r) => Number(r.age) < 16 || r.guardianFName || r.guardianEmail,
        fields: [
          ['guardianFName', 'Name'],
          ['guardianLName', 'Surname'],
          ['guardianEmail', 'Email'],
          ['guardianTelNo', 'Telephone'],
          ['guardianIdCard', 'ID card'],
        ],
      },
    ],
    files: [],
  },
  {
    key: 'dfm2026',
    name: 'DFM2026',
    what: 'DFM2026 community call applications',
    list: { url: '/api/dfm2026/applications', key: 'applications', paged: true },
    when: 'submittedAt',
    title: (r) => fullName(r.name, r.surname) || `Application #${r.id}`,
    subtitle: (r) => r.email,
    columns: [{ label: 'Status', value: (r) => (r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : '') }],
    sections: [
      {
        title: 'Applicant',
        fields: [
          ['name', 'Name'],
          ['surname', 'Surname'],
          ['age', 'Age'],
          ['email', 'Email'],
          ['contactNumber', 'Contact number'],
          ['status', 'Status'],
        ],
      },
      {
        title: 'Application',
        fields: [
          ['motivation', 'Motivation', 'long'],
          ['cvBio', 'CV (written)', 'long'],
        ],
      },
    ],
    files: [
      { column: 'cvPath', label: 'CV', url: (r) => (r.cvPath ? `/api/dfm2026/cv/${r.id}` : null), name: (r) => r.cvOriginalName || 'cv.pdf' },
      { column: 'videoPath', label: 'Video', url: (r) => (r.videoPath ? `/api/dfm2026/video/${r.id}` : null), name: (r) => r.videoOriginalName || 'video.zip' },
    ],
  },
];

export const archivedForm = (key) => ARCHIVED_FORMS.find((f) => f.key === key) || null;

/** Columns never shown as answers: internal flags, stored file paths and ids already shown. */
export const HIDDEN_COLUMNS = new Set(['id', 'isDeleted', 'cvPath', 'videoPath', 'cvOriginalName', 'videoOriginalName', 'locality']);
